<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="far fa-calendar-check" class="q-mb-sm" /> {{ $t('appointments') }}
		</div>

		<q-tabs v-model="tab">
			<q-tab slot="title" name="basic-data" :label="$t('basic_data')" />
			<q-tab slot="title" name="events" :label="$t('events')" v-if="showEvents" />
			<q-tab
				slot="title"
				name="reaction_tests"
				:label="$t('reaction_tests')"
				v-show="showReactionTests && permissions.presential_full_view"
			/>
			<q-tab
				slot="title"
				name="supportMaterial"
				:label="$t('support_materials')"
				v-if="showSupportMaterials && permissions.presential_full_view"
			/>
			<q-tab slot="title" name="guests" :label="$t('subscriptions')" v-if="showGuests" />
			<q-tab
				slot="title"
				name="materials"
				:label="$t('inputs')"
				v-if="showMaterials && permissions.presential_full_view"
			/>
			<q-tab
				slot="title"
				name="evidences"
				:label="$t('evidences')"
				v-if="showEvidences && permissions.presential_full_view"
			/>
			<q-tab
				slot="title"
				name="costs"
				:label="$t('costs')"
				v-if="showCosts && permissions.presential_full_view"
			/>
			<q-tab slot="title" name="comments" :label="$t('comments')" v-show="showComments" />
			<q-tab slot="title" name="presence" :label="$t('presence')" v-show="showPresence" />
			<q-tab slot="title" name="results" :label="$t('results')" v-if="showResults" />
			<q-tab
				slot="title"
				name="history"
				:label="$t('history')"
				v-if="showHistory && permissions.presential_full_view"
			/>

			<q-tab-pane style="padding: 5px 2px;" name="basic-data">
				<basic-data
					:auto_duplicate="auto_duplicate"
					:permissions="permissions"
					:full_name="full_name"
					:current_client="current_client"
					@autoDuplicateTraining="autoDuplicateTraining"
					@updateEventsInfo="updateEventsInfo"
					@changeTab="changeTab"
					@changeTimezone="changeTimezone"
					@AcademiaCoralApprovalHandler="AcademiaCoralApprovalHandler"
				/>
			</q-tab-pane>

			<q-tab-pane style="padding: 5px 2px;" name="events" :keep-alive="keep_alive_events">
				<events
					:permissions="permissions"
					:events_live_class_isPublic="events_live_class_isPublic"
					:events_title="events_title"
					:events_description="events_description"
					:status="status"
					:timezone="timezone"
					@keepAlive="keepAliveEvents"
					:event_to_finish="event_to_finish"
				/>
			</q-tab-pane>

			<q-tab-pane name="reaction_tests">
				<reaction-tests :permissions="permissions" :status="status" />
			</q-tab-pane>

			<q-tab-pane name="supportMaterial">
				<supportMaterial
					:manage_models="permissions.manage_models"
					:appointment_status="status"
				/>
			</q-tab-pane>

			<q-tab-pane name="guests">
				<guests
					:permissions="permissions"
					:allowed_profiles="allowed_profiles"
					:allowed_areas="allowed_areas"
					:current_client="current_client"
					:status="status"
				/>
			</q-tab-pane>

			<q-tab-pane name="materials">
				<materials
					:manage_models="permissions.manage_models"
					:status="status"
					:is_training_manager="permissions.trainings_manage"
				/>
			</q-tab-pane>

			<q-tab-pane name="evidences">
				<evidences :status="status" :is_training_manager="permissions.trainings_manage" />
			</q-tab-pane>

			<q-tab-pane name="costs">
				<costs :status="status" :is_training_manager="permissions.trainings_manage" />
			</q-tab-pane>

			<q-tab-pane name="comments">
				<comments origin="presential" :presential_status="status" />
			</q-tab-pane>

			<q-tab-pane name="presence">
				<presence :url="presenceUrl"/>
			</q-tab-pane>

			<q-tab-pane name="results">
				<results
					:status="status"
					:events_title="events_title"
					:max_participants="max_participants"
					@goToUnfinishedEvent="goToUnfinishedEvent"
					:permissions="permissions"
				/>
			</q-tab-pane>

			<q-tab-pane name="history">
				<history />
			</q-tab-pane>
		</q-tabs>

		<div class="row gutter-sm justify-end q-mt-xs">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')" v-if="showDeleteButton">
				<q-btn
					color="negative"
					class="full-width"
					:label="$t('remove')"
					icon="fal fa-trash"
					@click="show_delete_model = true"
					:disable="!permissions.request_events"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')" v-if="showDuplicateButton">
				<q-btn
					color="info"
					class="full-width"
					:label="$t('duplicate')"
					icon="fal fa-copy"
					@click="show_duplicate_modal = true"
					:disable="!permissions.request_events"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')" v-if="showCancelandReprove">
				<q-btn
					color="negative"
					class="full-width"
					:label="$t('cancel_appointment')"
					icon="far fa-times"
					@click="confirmCancelAppointment()"
				/>
			</div>

			<div
				class="col-xs-12 col-sm-3"
				:class="getOrder('cancel')"
				v-if="
					(showCancelandReprove && status === 1) || (showCancelandReprove && status === 2)
				"
			>
				<q-btn
					color="warning"
					class="full-width"
					:label="$t('reprove')"
					icon="far fa-undo"
					@click="confirmReproveAppointment()"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')" v-if="showApprovalButtons">
				<q-btn
					color="positive"
					class="full-width"
					:label="$t('approve')"
					icon="far fa-check"
					@click="confirmApproveAppointment()"
				/>
			</div>
		</div>

		<div class="row gutter-sm justify-end q-mt-xs">
			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="far fa-chevron-left"
					@click="$router.push({ name: 'appointments' })"
				/>
			</div>
		</div>

		<q-dialog v-model="show_approve" prevent-close>
			<span slot="title">{{ $t('approve_appointment') }}</span>

			<span slot="message">{{ $t('do_you_confirm_approving_this_appointment') }}</span>

			<div slot="body">
				<q-input
					type="textarea"
					:placeholder="$t('observation')"
					v-model="approve_observation"
				/>
			</div>

			<template slot="buttons" slot-scope="props">
				<q-btn color="tertiary" :label="$t('close')" @click="hideApproval()" />
				<q-btn color="positive" :label="$t('approve')" @click="saveApproval()" />
			</template>
		</q-dialog>

		<q-dialog v-model="show_reprove" prevent-close>
			<span slot="title">{{ $t('reprove_appointment') }}</span>

			<span slot="message">{{ $t('do_you_confirm_reproving_this_appointment') }}</span>

			<div slot="body">
				<q-input
					type="textarea"
					:placeholder="$t('observation')"
					v-model="reprove_observation"
					:error="$v.reprove_observation.$error"
					@input="$v.reprove_observation.$touch"
				/>
			</div>

			<template slot="buttons" slot-scope="props">
				<q-btn color="tertiary" :label="$t('close')" @click="hideReproval()" />
				<q-btn color="warning" :label="$t('reprove')" @click="saveReproval()" />
			</template>
		</q-dialog>

		<q-dialog v-model="show_cancel" prevent-close>
			<span slot="title">{{ $t('cancel_appointment') }}</span>

			<span slot="message">{{
				$t('do_you_confirm_canceling_this_appointment_this_action_cannot_be_undone')
			}}</span>

			<div slot="body">
				<q-input
					type="textarea"
					:placeholder="$t('observation')"
					v-model="cancel_observation"
					:error="$v.cancel_observation.$error"
					@input="$v.cancel_observation.$touch"
				/>
			</div>

			<template slot="buttons" slot-scope="props">
				<q-btn color="tertiary" :label="$t('close')" @click="hideCancel()" />
				<q-btn color="negative" :label="$t('cancel')" @click="saveCancel()" />
			</template>
		</q-dialog>

		<q-dialog v-model="show_delete_model" prevent-close>
			<span slot="title">{{ $t('remove_model') }}</span>

			<span slot="message">{{
				$t('do_you_confirm_removing_this_model_this_action_cannot_be_undone')
			}}</span>

			<template slot="buttons" slot-scope="props">
				<q-btn color="tertiary" :label="$t('cancel')" @click="show_delete_model = false" />
				<q-btn color="negative" :label="$t('remove')" @click="deleteModel()" />
			</template>
		</q-dialog>

		<duplicate :show="show_duplicate_modal" @hideDuplicate="hideDuplicate" />
	</q-page>
</template>

<script>
import BasicData from 'components/admin/presential/BasicData'
import Duplicate from 'components/admin/presential/Duplicate'
import Presence from 'components/admin/presential/Presence'
import { mapGetters } from 'vuex'
import { required, requiredIf } from 'vuelidate/lib/validators'
import * as moment from 'moment'
import * as tz from 'moment-timezone'

export default {
	name: 'Appointment',

	i18nOptions: {
		namespaces: [
			'custom',
			'presential',
			'webcast',
			'live_classes',
			'common',
			'admin',
			'training',
			'users',
		],
	},

	props: ['customTab'],

	components: {
		'basic-data': BasicData,
		duplicate: Duplicate,
		presence: Presence,
		events: () => import('components/admin/presential/Events'),
		'reaction-tests': () => import('components/admin/presential/tests/ReactionTests'),
		guests: () => import('components/admin/presential/Guests'),
		materials: () => import('components/admin/presential/Materials'),
		evidences: () => import('components/admin/presential/Evidences'),
		results: () => import('components/admin/presential/Results'),
		costs: () => import('components/admin/presential/Costs'),
		supportMaterial: () => import('components/admin/presential/SupportMaterialTab'),
		comments: () => import('components/admin/Comments'),
		history: () => import('components/admin/presential/History'),
	},

	data() {
		return {
			tab: 'basic-data',
			presential_event_id: '',
			status: null,
			type_action: '',
			show_cancel: false,
			cancel_observation: '',
			show_reprove: false,
			reprove_observation: '',
			show_approve: false,
			approve_observation: '',
			timezone: '',
			keep_alive_events: false,
			show_duplicate_modal: false,
			show_delete_model: false,
			events_title: '',
			events_description: '',
			events_live_class_isPublic: false,
			max_participants: null,
			current_client: '',
			auto_duplicate: false,
			show_coral_approval: true,
			allowed_areas: [],
			allowed_profiles: [],
			event_to_finish: null,
			permissions: {
				approve_events: false,
				cancel_events: false,
				edit_tests: false,
				finish_events: false,
				manage_models: false,
				presential_edit_basic_data: false,
				presential_edit_evaluations: false,
				presential_edit_events: false,
				presential_full_view: false,
				presential_import_results: false,
				presential_print_results: false,
				presential_print_users: false,
				presential_save_results: false,
				request_events: false,
				subscribe_event_users: false,
				trainings_manage: false,
				view_pre_scheduled_training: false,
			},
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			user_module_items: 'common/user_module_items',
			user_clients: 'common/user_clients',
			user_client_module_items: 'common/user_client_module_items',
			full_name: 'common/full_name',
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		presenceUrl() {
			let client = this.clients.find((client) => client.id == this.id_user_client_selected)
			return `${client.presential_qrcode_url_prefix}?presential_event_id=${this.presential_event_id}`
		},

		showEvents() {
			return this.$route.params.id != 0 ? true : false
		},

		showReactionTests() {
			return this.$route.params.id != 0 ? true : false
		},

		showSupportMaterials() {
			return this.$route.params.id != 0 ? true : false
		},

		showGuests() {
			if (this.$route.params.id != 0 && this.status !== 9) return true
			else return false
		},

		showMaterials() {
			if (this.$route.params.id != 0) return true
			else return false
		},

		showEvidences() {
			if (this.status === 5 || this.status === 8) return true
			else return false
		},

		showResults() {
			if (this.status === 3 || this.status === 4 || this.status === 5 || this.status === 8)
				return true
			else return false
		},

		showCosts() {
			if (this.status === 5 || this.status === 8) return true
			else return false
		},

		showComments() {
			if (this.status >= 5 && this.status !== 9) return true
			else return false
		},

		showPresence() {
			let client = this.clients.find((client) => client.id == this.id_user_client_selected)
			if ((this.status == 2 || this.status == 3 || this.status == 4 || this.status == 5 || this.status == 8) && client.presential_qrcode_url_prefix !== '') return true
			else return false
		},

		showDuplicate() {
			if (this.$route.params.id != 0) return true
			else return false
		},

		showHistory() {
			if (this.$route.params.id != 0 && this.status !== 9) return true
			else return false
		},

		showApprovalButtons() {
			if (this.current_client === 'Academia Coral' && !this.show_coral_approval) return false
			if (this.status >= 2) return false
			if (
				this.presential_event_id == 0 ||
				(this.tab !== 'basic-data' && this.tab !== 'history')
			)
				return false
			else return this.permissions.approve_events
		},

		showCancelandReprove() {
			if (this.status === 7 || this.status === 8 || this.status === 9) return false
			else if (
				this.presential_event_id == 0 ||
				(this.tab !== 'basic-data' && this.tab !== 'history')
			)
				return false
			else return this.permissions.cancel_events
		},

		showDuplicateButton() {
			if (this.presential_event_id == 0 || this.tab !== 'basic-data') return false
			else return this.permissions.approve_events
		},

		showDeleteButton() {
			if (this.status === 9 && this.permissions.approve_events) return true
			else return false
		},
	},

	methods: {
		confirmCancelAppointment() {
			this.type_action = 'cancelation'
			this.show_cancel = true
		},

		saveCancel() {
			this.$v.cancel_observation.$touch()

			if (!this.$v.cancel_observation.$error) {
				this.cancelAppointment()
			}
		},

		cancelAppointment() {
			const fd = new FormData()

			fd.append('cancel_observation', this.cancel_observation)

			this.$axios
				.post(
					`/presential/presential_events/${this.presential_event_id}/cancel_event/`,
					fd,
					this.getHeader
				)
				.then((response) => {
					this.status = response.data.status
					this.type_action = ''
					this.$q.notify({
						message: this.$t('appointment_cancelation_success'),
						type: 'positive',
						icon: 'fal fa-check',
					})
				})
				.catch((e) => {
					console.log(e)

					this.$q.notify({
						message: this.$t('appointment_cancelation_error'),
						type: 'negative',
						icon: 'fal fa-ban',
					})
				})
				.finally(() => {
					this.show_cancel = false
					this.$q.loading.hide()
					this.$router.push({ name: 'appointments' })
				})
		},

		hideCancel() {
			this.$v.$reset()
			this.type_action = ''
			this.show_cancel = false
		},

		hideDuplicate() {
			this.show_duplicate_modal = false
		},

		confirmReproveAppointment() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.show_reprove = true
				this.type_action = 'reproval'
			}
		},

		saveReproval() {
			this.$v.reprove_observation.$touch()

			if (!this.$v.reprove_observation.$error) {
				this.reproveAppointment()
			}
		},

		reproveAppointment() {
			this.$q.loading.show()
			let fd = new FormData()

			fd.append('reprove_observation', this.reprove_observation)

			this.$axios
				.post(
					`/presential/presential_events/${this.presential_event_id}/reprove_event/`,
					fd,
					this.getHeader
				)
				.then((response) => {
					this.status = response.data.status
					this.type_action = ''
					this.$q.notify({
						message: this.$t('appointment_reproval_success'),
						type: 'positive',
						icon: 'fal fa-check',
					})
				})
				.catch((e) => {
					console.log(e)

					this.$q.notify({
						message: this.$t('appointment_reproval_error'),
						type: 'negative',
						icon: 'fal fa-ban',
					})
				})
				.finally(() => {
					this.show_cancel = false
					this.$q.loading.hide()
					this.$router.push({ name: 'appointments' })
				})
		},

		hideReproval() {
			this.$v.$reset()
			this.type_action = ''
			this.show_reprove = false
		},

		confirmApproveAppointment() {
			this.$q.loading.show()

			this.$axios
				.get(
					`/presential/presential_events/${this.presential_event_id}/event_dates/`,
					this.getHeader
				)
				.then((response) => {
					if (response.data.length > 0) {
						let r = response.data
						let event_ids = []
						let activity_ids = []
						r.map((e) => {
							if (e.presential_event_date_type === 2) {
								event_ids.push(e.id)
								if (e.webcast) {
									activity_ids.push(e.webcast.id)
								}
							} else if (e.presential_event_date_type === 3) {
								event_ids.push(e.id)
								if (e.live_class) {
									activity_ids.push(e.live_class.id)
								}
							}
						})

						if (event_ids.length === activity_ids.length) {
							this.show_approve = true
						} else {
							this.tab = 'events'

							this.$q.notify({
								message: this.$t('you_need_to_save_all_events_first'),
								type: 'negative',
								icon: 'fal fa-ban',
							})
						}
					} else {
						this.tab = 'events'

						this.$q.notify({
							message: this.$t(
								'at_least_one_event_is_required_to_approve_the_appointment'
							),
							type: 'negative',
							icon: 'fal fa-ban',
						})
					}
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		saveApproval() {
			this.type_action = 'approval'
			this.aproveAppointment()
		},

		aproveAppointment() {
			this.$q.loading.show()
			let fd = new FormData()

			fd.append('approve_observation', this.approve_observation)

			this.$axios
				.post(
					`/presential/presential_events/${this.presential_event_id}/approve_event/`,
					fd,
					this.getHeader
				)
				.then((response) => {
					this.status = response.data.status
					this.type_action = ''
					this.$q.notify({
						message: this.$t('appointment_aproval_success'),
						type: 'positive',
						icon: 'fal fa-check',
					})
				})
				.catch((e) => {
					console.log(e)

					this.$q.notify({
						message: this.$t('appointment_aproval_error'),
						type: 'negative',
						icon: 'fal fa-ban',
					})
				})
				.finally(() => {
					this.show_cancel = false

					this.$q.loading.hide()
					this.$router.push({ name: 'appointments' })
				})
		},

		deleteModel() {
			this.show_delete_model = false
			this.$q.loading.show()

			this.$axios
				.delete(
					`/presential/presential_events/${this.presential_event_id}/`,
					this.getHeader
				)
				.then(() => {
					this.$q.loading.hide()
					this.$router.push({ name: 'appointments' })
				})
				.catch((e) => {
					this.$q.loading.show()
					console.log(e)
				})
		},

		hideApproval() {
			this.type_action = ''
			this.show_approve = false
		},

		updateEventsInfo({
			id,
			status,
			title,
			description,
			liveClassIsPublic,
			maxParticipants,
			allowed_areas,
			allowed_profiles,
		}) {
			this.presential_event_id = id
			this.status = status
			this.events_title = title
			this.events_description = description
			this.events_live_class_isPublic = liveClassIsPublic
			this.max_participants = maxParticipants
			this.allowed_areas = allowed_areas
			this.allowed_profiles = allowed_profiles
		},

		changeTimezone(timezone) {
			this.timezone = timezone
		},

		getOrder(name) {
			if (name == 'cancel') {
				return this.$q.platform.is.mobile ? 'order-last' : 'order-first'
			} else if (name == 'save') {
				return this.$q.platform.is.mobile ? 'order-first' : 'order-last'
			}
		},

		keepAliveEvents(status) {
			this.keep_alive_events = status
		},

		changeTab(tab) {
			this.tab = tab
		},

		autoDuplicateTraining() {
			this.show_duplicate_modal = true
		},

		enableCustomClientCheck() {
			this.current_client = process.env.PROJECT_FOLDER
		},

		checkPermissions() {
			const client = this.user_client_module_items.find(
				(obj) => obj.id === this.id_user_client_selected
			)
			if (client) {
				for (const [key, value] of Object.entries(this.permissions)) {
					const obj = client.module_items.find((obj) => obj.slug === key)
					if (obj) {
						this.permissions[key] = obj.permission
					}
				}
			} else return
		},

		AcademiaCoralApprovalHandler(status) {
			if (status) this.show_coral_approval = true
			else this.show_coral_approval = false
		},

		goToUnfinishedEvent(event_id) {
			this.event_to_finish = event_id
			this.tab = 'events'
		},
	},

	created() {
		this.presential_event_id = this.$route.params.id

		if (this.$route.params.permissions) {
			this.permissions = this.$route.params.permissions
		} else this.checkPermissions()

		this.enableCustomClientCheck()

		if (this.$route.params.autoDuplicate) {
			this.auto_duplicate = true
		}
	},

	watch: {
		$route: function() {
			this.presential_event_id = this.$route.params.id
			this.event_to_finish = null
			if (this.auto_duplicate) {
				this.auto_duplicate = false
			}
		},
	},

	validations() {
		return {
			cancel_observation: {
				requiredIf: requiredIf(() => {
					return this.type_action === 'cancelation' ? true : false
				}),
			},
			reprove_observation: {
				requiredIf: requiredIf(() => {
					return this.type_action === 'reproval' ? true : false
				}),
			},
		}
	},
}
</script>

<style></style>
